import { Navigate } from 'react-router';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { canSeeTile, type TileSlug } from './visibility';
import { isTileOn, useModuleTree } from '@/shared/modules/store';
import { NotFoundPage } from '@/features/NotFoundPage';

interface Props {
  tile: TileSlug;
  children: ReactNode;
}

/**
 * Route-level tile gate — mirrors the PHP `hubVisibleTiles()` server-side
 * include guard so that hand-typing `/hr` or `/management` cannot bypass
 * rail-visibility. Compose with `<ProtectedRoute>` for unauth → /login;
 * this component handles auth'd-but-unauthorized → /forbidden.
 *
 * The loading branch of `<ProtectedRoute>` runs first, so by the time
 * this component renders `useAuth()` has resolved. We still defensively
 * delegate to `canSeeTile` (which returns false for `null`) — that way
 * the gate is closed by default.
 */
export function RequireTile({ tile, children }: Props) {
  const { user } = useAuth();
  const tree = useModuleTree();

  // A module the tenant switched off (Admin ▸ Modules) does not exist for
  // anyone: not-found, never /forbidden, so nobody learns it is there.
  if (!isTileOn(tile, tree)) return <NotFoundPage />;

  if (!canSeeTile(tile, user)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
